import axios, { AxiosHeaders } from "axios";

// When the browser routes MW calls through the proxy, an absolute MW URL must
// become a path under /api/mw.
const toMwPath = (url) => {
  try {
    const base = new URL(process.env.NEXT_PUBLIC_API_BASEURL);
    const target = new URL(url, base);
    if (target.origin !== base.origin) return url;
    const prefix = base.pathname.replace(/\/$/, "");
    return (target.pathname.startsWith(prefix)
      ? target.pathname.slice(prefix.length)
      : target.pathname) + target.search;
  } catch {
    return url;
  }
};

const client = axios.create({ baseURL: process.env.NEXT_PUBLIC_API_BASEURL });

export const request = async ({ ...options }) => {
  client.defaults.headers.common.Authorization = `Bearer token`;
  const onSuccess = (response) => ({
    data: response.data,
    error: null,
    status: response?.status,
  });
  const onError = (error) => {
    // catch error here !!

    return { data: null, error: error?.message };
  };
  return client(options).then(onSuccess).catch(onError);
};

const axiosRequest = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASEURL,
  maxBodyLength: Infinity,
  headers: {
    "Content-Type": "application/json",
  },
});

// Compare origins, not prefixes: the base URL carries a path and some callers
// concatenate onto it, while other hosts (e.g. Typesense) must never get the MW token.
const isMwRequest = (config) => {
  try {
    const base = window.location.origin;
    const targetOrigin = new URL(axiosRequest.getUri(config), base).origin;
    return targetOrigin === new URL(process.env.NEXT_PUBLIC_API_BASEURL, base).origin;
  } catch {
    return false;
  }
};

// ar is the default locale and has no path prefix.
const getPageLocale = () =>
  /^\/en(\/|$)/.test(window.location.pathname) ? "en" : "ar";

axiosRequest.interceptors.request.use(
  async (config) => {
    if (typeof window === "object") {
      if (!isMwRequest(config)) {
        return config;
      }
      const headers = AxiosHeaders.from(config.headers);
      headers.set("x-locale", getPageLocale());
      // The server adds the real credentials; drop anything the client set.
      headers.delete("Authorization");
      headers.delete("mage");
      headers.delete("admin");
      config.headers = headers;
      config.baseURL = "/api/mw";
      config.url = toMwPath(config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosRequest.interceptors.response.use(
  (res) => {
    return Promise.resolve({ data: res.data, error: null, status: res.status });
  },
  (error) => {
    if (error.response) {
      if (error.response?.data?.message?.includes("The consumer isn't authorized to access")){
        if(typeof window === 'object'){
          window?.localStorage?.clear()
          window?.sessionStorage?.clear()
          window.document.cookie = ''
          // The auth cookies are HttpOnly, so only the server can clear them.
          fetch("/api/auth/logout", { method: "POST" }).catch(() => {})
        }
      }
        // Handle HTTP errors

        return Promise.reject({
          data: null,
          err: error,
          error: error.response?.data?.message || error.message,
          status: error.response.status,
        });
    } else if (error.request) {
      // Handle network errors
      return Promise.reject({
        data: null,
        err: error,
        error: error.request?.data?.message || error.message,
        status: error.request.status,
      });
    } else {
      // Handle other errors
      return Promise.reject({
        data: null,
        err: error,
        error: error?.message,
        status: 500,
      });
    }
  }
);

export default axiosRequest;
